import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * VRG-545 — mirrors `SignupAllowlistConversionStatus` (apps/api domain). Three
 * states, not a boolean: an account with no company is a half-finished signup and
 * belongs in neither neighbour. That file's docblock holds the reasoning for why
 * the line for "converted" is a COMPANY and not a paid plan.
 */
export type SignupAllowlistConversionStatus = 'not_signed_up' | 'signed_up' | 'converted'

export type SignupAllowlistEntry = {
  id: string
  email: string
  added_by_user_id: string | null
  /**
   * When the email was added to the ALLOWLIST.
   *
   * ⚠️ NOT when they signed up — that is `signed_up_at`. Two different dates on
   * one row, which is why the grid labels them "Added to allowlist" and
   * "Signed up" rather than leaving either as a bare "Date".
   */
  created_at: string
  conversion_status: SignupAllowlistConversionStatus
  /** `users.created_at` — when the account was actually created. Null if never. */
  signed_up_at: string | null
  company: {
    id: string
    name: string
    logo: string | null
    subdomain: string | null
    /**
     * VRG-639 — `companies.became_client_posthog_status`. `'sent'` = the company's
     * `became_client` reached the website's PostHog (rendered as "Tracked").
     */
    tracking_status: string | null
  } | null
  subscription: {
    id: string
    plan_title: string | null
    plan_interval: string | null
    status: string
    /** Subscription start (its own `created_at`), NOT `current_period_start`. */
    started_at: string | null
  } | null
}

type SignupAllowlistResponse = SignupAllowlistEntry[]

/**
 * What POST returns — the bare row, with none of the conversion fields.
 *
 * Deliberately its own type rather than a cast to `SignupAllowlistEntry`: an
 * email added a second ago cannot have converted, so the endpoint resolves
 * nothing, and typing the response as the enriched shape would put four fields on
 * it that are always absent. The mutation invalidates the list query anyway, so
 * the enriched row arrives from the refetch.
 */
export type SignupAllowlistAddResponse = Pick<
  SignupAllowlistEntry,
  'id' | 'email' | 'added_by_user_id' | 'created_at'
>

export const SIGNUP_ALLOWLIST_QUERY_KEY = 'signup-allowlist'

export const useSignupAllowlist = () => {
  const queryClient = useQueryClient()

  const query = useQuery<SignupAllowlistResponse>({
    queryKey: [SIGNUP_ALLOWLIST_QUERY_KEY],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/internal/signup-allowlist',
      })
      return response.data as SignupAllowlistResponse
    },
    staleTime: 1000 * 60, // 1 minute
  })

  const addMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/internal/signup-allowlist',
        data: { email },
      })
      return response.data as SignupAllowlistAddResponse
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SIGNUP_ALLOWLIST_QUERY_KEY] })
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: '/internal/signup-allowlist',
        data: { email },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SIGNUP_ALLOWLIST_QUERY_KEY] })
    },
  })

  return {
    entries: query.data ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
    add: addMutation.mutate,
    addAsync: addMutation.mutateAsync,
    isAdding: addMutation.isPending,
    remove: removeMutation.mutate,
    removeAsync: removeMutation.mutateAsync,
    isRemoving: removeMutation.isPending,
  }
}
